import dayjs, {utc} from 'dayjs'

export const countryCurrencyEnum = {
  US: 'USD',
  AU: 'AUD',
  CA: 'CAD',
  NZ: 'NZD',
  GB: 'GBP',
  ES: 'USD',
  IT: 'USD',
  IN: 'USD',
  PH: 'USD',
  VN: 'USD',
  GR: 'USD',
}

//  Currency symbols map
export const currencySymbolsEnum = {
  USD: '$',
  AUD: '$',
  CAD: '$',
  NZD: '$',
  BBD: '$',
  KYD: '$',
  GBP: '£',
}

// Web Design Fee - Entitlements
export const WEBSITE_DESIGN_FEE_ENTITLEMENT = {
  code: 'thryvdesg2',
  id: 'szppk0uzmej8',
  name: 'Website Design Fee',
  currencies: [
    {
      currency: 'AUD',
      unitAmount: 209,
    },
    {
      currency: 'CAD',
      unitAmount: 179,
    },
    {
      currency: 'NZD',
      unitAmount: 229,
    },
    {
      currency: 'USD',
      unitAmount: 149,
    },
    {
      currency: 'BBD',
      unitAmount: 298,
    },
    {
      currency: 'KYD',
      unitAmount: 122.18,
    },
  ],
  appliesTo: ['addon-5-website_difm', 'ent-5-website'],
  disabled: true, // can't be manually removed
  selected: true,
  quantity: 1,
  unitAmount: 149,
  amount: 149,
}

export const onboardingFeeCode = 'thryvprfee'

export const DEFAULT_KICKOFF_CALL_LABEL =
  'SEO / Leads / Social Ads / Thryv Social Posts Kickoff Call'

const KICKOFF_CALL_PRODUCTS = [
  {match: 'AEO & SEO', label: 'AEO & SEO'},
  {match: 'Thryv Leads', label: 'Leads'},
  {match: 'Social Ads', label: 'Social Ads'},
  {match: 'Thryv Social Posts', label: 'Thryv Social Posts'},
  {match: 'Marketing Center', label: 'Marketing Center'},
  {match: 'Workforce Center', label: 'Workforce Center'},
  {match: 'Thryv HVAC', label: 'Thryv HVAC'},
  {match: 'Business Center', label: 'Business Center'},
  {match: 'Professionally Designed Website', label: 'Website'},
]

const CENTER_KICKOFF_LABELS = [
  'Marketing Center',
  'Workforce Center',
  'Business Center',
]

export const getKickoffCallLabelFromCart = structuredCart => {
  if (!structuredCart) return DEFAULT_KICKOFF_CALL_LABEL

  const cartItems = [
    ...(structuredCart.centers || []),
    ...(structuredCart.apps || []),
    ...(structuredCart.oneTimeFees || []),
  ]

  const namesInCart = []
  const addName = value => {
    if (typeof value === 'string' && value.trim()) namesInCart.push(value)
  }

  cartItems.forEach(item => {
    if (!item) return

    addName(item.name)

    if (Array.isArray(item.addons)) {
      item.addons.forEach(addon => addName(addon?.name))
    }

    if (Array.isArray(item.entitlements)) {
      item.entitlements.forEach(entitlement => {
        if (typeof entitlement === 'string') {
          addName(entitlement)
        } else {
          addName(entitlement?.name)
        }
      })
    }
  })

  const selectedLabels = KICKOFF_CALL_PRODUCTS.filter(({match}) =>
    namesInCart.some(name => name.toLowerCase().includes(match.toLowerCase())),
  ).map(({label}) => label)

  const orderedSelectedLabels = [
    ...selectedLabels.filter(label => CENTER_KICKOFF_LABELS.includes(label)),
    ...selectedLabels.filter(label => !CENTER_KICKOFF_LABELS.includes(label)),
  ]

  if (!orderedSelectedLabels.length) return DEFAULT_KICKOFF_CALL_LABEL

  return `${orderedSelectedLabels.join(' / ')} Kickoff Call`
}

export const getItemFromCart = ({
  cartCentersAndApps,
  groupKey,
  items = [],
  predicate,
}) => {
  const normalizedGroupKey = (groupKey || '').toLowerCase()
  const itemCodes = new Set(
    (items || []).map(item => item?.code).filter(Boolean),
  )

  const matches = item => {
    if (!item) return false
    if (itemCodes.size > 0 && !itemCodes.has(item.code)) return false
    if ((item?.groupKey || '').toLowerCase() !== normalizedGroupKey)
      return false
    if (predicate && !predicate(item)) return false
    return true
  }

  const findIn = section => (section || []).find(matches)

  return (
    findIn(cartCentersAndApps?.centers) ||
    findIn(cartCentersAndApps?.apps) ||
    findIn(cartCentersAndApps?.oneTimeFees) ||
    null
  )
}

export const normalizeQuantity = (quantity, fallback = 1) => {
  const parsed = Number(quantity)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

export const getCartItemQuantityByCode = (cartCentersAndApps, code) => {
  if (!cartCentersAndApps || !code) return 0

  const allItems = [
    ...(cartCentersAndApps?.centers || []),
    ...(cartCentersAndApps?.apps || []),
    ...(cartCentersAndApps?.oneTimeFees || []),
  ]

  return allItems.reduce((total, item) => {
    if (item?.code !== code) return total
    return total + normalizeQuantity(item?.quantity)
  }, 0)
}

export const isItemAtMaxQty = ({cartCentersAndApps, item}) => {
  const maxQty = Number(item?.maxQty)
  if (!Number.isFinite(maxQty) || maxQty <= 0) return false

  const quantityInCart = getCartItemQuantityByCode(
    cartCentersAndApps,
    item?.code,
  )
  return quantityInCart >= maxQty
}

// Default addons for Growth Package
export const thryvAddonsList = [
  {
    code: 'appmarket-5-thryv-m2m',
    name: 'Thryv Add-Ons',
    // name: 'Thryv Marketplace',
    quantity: 1,
    unitAmount: 0,
    amount: 0,
    entitlements: [
      {
        code: 'addon-5-signatures_usage',
        name: 'Signatures Usage',
        quantity: 1,
        unitAmount: 0,
        amount: 0,
      },
      {
        code: 'app-websitebuilder',
        name: 'Website Builder',
        quantity: 1,
        unitAmount: 0,
        amount: 0,
      },
    ],
  },
]

export const updateGroupDataWithAlreadyAddedCart = (groupData, cart) => {
  // Create a lookup map for centers and apps by code
  const cartItemsMap = {}

  // Map centers
  cart.centers.forEach(center => {
    cartItemsMap[center.code] = center.isAlreadyAdded
    // Also map addons if any
    if (center.addons && center.addons.length) {
      center.addons.forEach(addon => {
        cartItemsMap[addon.code] = addon.isAlreadyAdded
      })
    }
  })

  // Map apps
  cart.apps.forEach(app => {
    cartItemsMap[app.code] = app.isAlreadyAdded
    // Also map app's addons
    if (app.addons && app.addons.length) {
      app.addons.forEach(addon => {
        cartItemsMap[addon.code] = addon.isAlreadyAdded
      })
    }
  })

  // Update GroupData
  return groupData.map(group => {
    // Update items
    const updatedItems = group.items.map(item => {
      const isAdded = cartItemsMap[item.code] || false

      // Update entitlements if they exist
      const updatedEntitlements = item.entitlements?.map(ent => {
        return {
          ...ent,
          isAlreadyAdded: cartItemsMap[ent.code] || false,
        }
      })

      return {
        ...item,
        isAlreadyAdded: isAdded,
        entitlements: updatedEntitlements,
      }
    })

    return {
      ...group,
      items: updatedItems,
    }
  })
}

export const restructureCart = (cart, allGroupCustomfieldMap = {}) => {
  if (!cart) return cart

  const apps = cart.apps || []

  // Check if there's already a Thryv Addons (appmarket-5-thryv-m2m)
  const thryvAddonIndex = apps.findIndex(
    app => app.code === 'appmarket-5-thryv-m2m',
  )
  const growthPackageIndex = apps.findIndex(
    app => app.groupKey === 'growthPackage',
  )

  // If both Thryv Addons and Growth Package exist
  if (thryvAddonIndex !== -1 && growthPackageIndex !== -1) {
    const thryvAddon = apps[thryvAddonIndex]
    const growthPackage = apps[growthPackageIndex]

    // Add growth package under Thryv Addons as addon
    const updatedThryvAddon = {
      ...thryvAddon,
      addons: [
        ...(thryvAddon.addons || []),
        {
          id: growthPackage.id,
          code: growthPackage.code,
          name: growthPackage.name,
          groupKey: growthPackage.groupKey,
          section: growthPackage.section,
          unitAmount: growthPackage.unitAmount,
          quantity: growthPackage.quantity,
        },
      ],
    }

    // Remove the old growth package entry
    const updatedApps = apps.filter(
      (a, i) => i !== growthPackageIndex && i !== thryvAddonIndex,
    )

    // Add updated Thryv Addons back
    updatedApps.push(updatedThryvAddon)

    return {...cart, apps: updatedApps}
  }

  //  If Thryv Addons doesn’t exist but growth package does —-> parent-child
  const updatedApps = apps.map(app => {
    if (app.groupKey === 'growthPackage') {
      const appMarket = app.addons?.find(
        a => a.code === 'appmarket-5-thryv-m2m',
      )

      if (appMarket) {
        const otherAddons = app.addons.filter(
          a => a.code !== 'appmarket-5-thryv-m2m',
        )

        return {
          ...appMarket,
          groupKey: app.groupKey,
          section: app.section,
          addons: [
            ...otherAddons,
            {
              id: app.id,
              code: app.code,
              name: app.name,
              groupKey: app.groupKey,
              section: app.section,
              unitAmount: app.unitAmount,
              amount: app.amount,
              quantity: app.quantity,
            },
          ],
        }
      }
    }

    return app
  })

  // update cart apps with group customfield
  const updatedAppsWithGroup = updatedApps?.map(app => {
    const groupCustomFields = allGroupCustomfieldMap[app.groupKey] || {}
    const isSeo = app.groupKey === 'aeo & seo' ? true : false

    if (isSeo) {
      return {
        ...app,
        customFields: groupCustomFields,
        addons: groupCustomFields?.addons || [],
      }
    } else {
      return {
        ...app,
        customFields: groupCustomFields,
      }
    }
  })
  // update cart centers with group customfield
  const updatedCentersWithGroup = cart.centers?.map(center => {
    const groupCustomFields = allGroupCustomfieldMap[center.groupKey] || {}
    return {
      ...center,
      customFields: groupCustomFields,
    }
  })

  return {...cart, centers: updatedCentersWithGroup, apps: updatedAppsWithGroup}
}

// // // export const restructureCart = (cart, groupData = {}) => {
// // //   // if (!cart) return cart
// // //   if (!cart) return cart

// // //   const updatedApps = []
// // //   cart.apps?.forEach(app => {
// // //     if (app.groupKey === 'growthPackage') {
// // //       // find appmarket-5-thryv-m2m addon
// // //       const appMarket = app.addons?.find(
// // //         a => a.code === 'appmarket-5-thryv-m2m',
// // //       )

// // //       if (appMarket) {
// // //         const otherAddons = app.addons.filter(
// // //           a => a.code !== 'appmarket-5-thryv-m2m',
// // //         )

// // //         const parentApp = {
// // //           ...appMarket,
// // //           groupKey: app.groupKey,
// // //           section: app.section,
// // //           addons: [
// // //             ...otherAddons,
// // //             {
// // //               id: app.id,
// // //               code: app.code,
// // //               name: app.name,
// // //               currencies: app.currencies,
// // //               entitlements: app.entitlements,
// // //               requiresCenter: app.requiresCenter,
// // //               groupKey: app.groupKey,
// // //               section: app.section,
// // //             },
// // //           ],
// // //         }

// // //         updatedApps.push(parentApp)
// // //       } else {
// // //         updatedApps.push(app)
// // //       }
// // //     } else {
// // //       updatedApps.push(app)
// // //     }
// // //   })

// // //   return {...cart, apps: updatedApps}

// // //   // const updatedApps = cart.apps?.map(app => {
// // //   //   let updatedApp = {...app}

// // //   //   // Handle parent-child structure for growthPackage / thryvLeads
// // //   //   if (app.groupKey === 'growthPackage') {
// // //   //     const appMarket = app.addons?.find(
// // //   //       a => a.code === 'appmarket-5-thryv-m2m',
// // //   //     )

// // //   //     if (appMarket) {
// // //   //       const otherAddons = app.addons.filter(
// // //   //         a => a.code !== 'appmarket-5-thryv-m2m',
// // //   //       )

// // //   //       updatedApp = {
// // //   //         ...appMarket,
// // //   //         groupKey: app.groupKey,
// // //   //         section: app.section,
// // //   //         addons: [
// // //   //           ...otherAddons,
// // //   //           {
// // //   //             id: app.id,
// // //   //             code: app.code,
// // //   //             name: app.name,
// // //   //             currencies: app.currencies,
// // //   //             entitlements: app.entitlements,
// // //   //             requiresCenter: app.requiresCenter,
// // //   //             groupKey: app.groupKey,
// // //   //             section: app.section,
// // //   //             addons: [],
// // //   //           },
// // //   //         ],
// // //   //       }
// // //   //     }
// // //   //   }

// // //   //   return updatedApp
// // //   // })

// // //   // return {
// // //   //   ...cart,
// // //   //   apps: [...remainingApps, ...groupedGrowthPackages],
// // //   //   centers: cart.centers || [],
// // //   // }
// // // }

// export const restructureCart = (cart, groupData = {}) => {
//   if (!cart) return cart

//   const updatedApps = cart.apps?.map(app => {
//     return {
//       ...app,
//     }
//   })

//   const updatedCenters = cart.centers?.map(center => {
//     return {
//       ...center,
//     }
//   })

//   return {
//     ...cart,
//     apps: updatedApps,
//     centers: updatedCenters || cart.centers,
//   }
// }

// export const restructureCart = (cart, groupData = {}) => {
//   if (!cart) return cart

//   const updatedApps = cart.apps?.map(app => {
//     let updatedApp = {...app}

//     // // Handle parent-child structure for growthPackage / thryvLeads
//     // if (app.groupKey === 'growthPackage' || app.groupKey === 'thryvLeads') {
//     //   const appMarket = app.selectedEntitlements?.find(
//     //     a => a.code === 'appmarket-5-thryv-m2m',
//     //   )

//     //   if (appMarket) {
//     //     const otherAddons = app.selectedEntitlements.filter(
//     //       a => a.code !== 'appmarket-5-thryv-m2m',
//     //     )

//     //     updatedApp = {
//     //       ...appMarket,
//     //       selectedEntitlements: [
//     //         ...otherAddons,
//     //         {
//     //           id: app.id,
//     //           code: app.code,
//     //           name: app.name,
//     //           currencies: app.currencies,
//     //           entitlements: app.entitlements,
//     //           requiresCenter: app.requiresCenter,
//     //           groupKey: app.groupKey,
//     //           section: app.section,
//     //           addons: [],
//     //         },
//     //       ],
//     //     }
//     //   }
//     // }

//     // Inject groupData as customFields
//     const groupFields = groupData[app.groupKey] || {}
//     updatedApp.customFields = groupFields

//     return updatedApp
//   })

//   // Similarly, you can inject custom fields into centers if needed
//   const updatedCenters = cart.centers?.map(center => {
//     const groupFields = groupData[center.groupKey] || {}
//     return {...center, customFields: groupFields}
//   })

//   return {
//     ...cart,
//     apps: updatedApps,
//     centers: updatedCenters || cart.centers,
//   }
// }

// export const restructureCart = cart => {
//   if (!cart) return cart

//   const updatedApps = []
//   cart.apps?.forEach(app => {
//     if (app.groupKey === 'growthPackage' || app.groupKey === 'thryvLeads') {
//       // find appmarket-5-thryv-m2m addon
//       const appMarket = app.selectedEntitlements?.find(
//         a => a.code === 'appmarket-5-thryv-m2m',
//       )

//       if (appMarket) {
//         const otherAddons = app.selectedEntitlements.filter(
//           a => a.code !== 'appmarket-5-thryv-m2m',
//         )

//         const parentApp = {
//           ...appMarket,
//           selectedEntitlements: [
//             ...otherAddons,
//             {
//               id: app.id,
//               code: app.code,
//               name: app.name,
//               currencies: app.currencies,
//               entitlements: app.entitlements,
//               requiresCenter: app.requiresCenter,
//               groupKey: app.groupKey,
//               section: app.section,
//               // selectedEntitlements: app.selectedEntitlements,
//               addons: [],
//             },
//           ],
//         }

//         updatedApps.push(parentApp)
//       } else {
//         updatedApps.push(app)
//       }
//     } else {
//       updatedApps.push(app)
//     }
//   })

//   return {...cart, apps: updatedApps}
// }

export const formatCartToPayload = (
  currency = 'USD',
  cartObj,
  allCoupons,
  // defaultObFeeCoupon,
  // PlanIncentive,
  // promotions,
) => {
  const formatItem = (item, type) => {
    const currencyObj =
      item.currencies?.find(c => c.currency === currency) || {}
    return {
      type,
      name: item.name,
      code: item.code,
      quantity: 1,
      currency,
      unitAmount: currencyObj.unitAmount || 0,
      addons: (item.selectedEntitlements || []).map(addon => {
        const addonCurrencyObj =
          addon.currencies?.find(c => c.currency === currency) || {}
        return {
          type,
          id: addon.id,
          code: addon.code,
          name: addon.name,
          currencies: addon.currencies || [],
          unitAmount: addonCurrencyObj.unitAmount ?? 0,
          quantity: addon.quantity || 1,
        }
      }),
    }
  }

  return {
    planCode: (cartObj.centers || []).map(center =>
      formatItem(center, 'centers'),
    ),
    addOnCode: (cartObj.apps || []).map(app => formatItem(app, 'apps')),
    oneTimeFees: (cartObj.oneTimeFees || []).map(fee =>
      formatItem(fee, 'oneTimeFees'),
    ),
    couponCode: [
      // ...allCoupons?.PlanIncentive?.map(incentive => incentive.code),
      // ...allCoupons?.promotions?.map(coupon => coupon.code),
    ],
    defaultIncentive: allCoupons?.defaultObFeeCoupon?.map(
      coupon => coupon.code,
    ),
  }
}

// export const isCartEmpty = cart => {
//   if (!cart) return true
//   const {apps = [], centers = [], onetimeFees = []} = cart
//   return apps.length === 0 && centers.length === 0 && onetimeFees.length === 0
// }

export const isCartEmpty = cart => {
  if (!cart) return true

  const {apps = [], centers = [], oneTimeFees = []} = cart

  // Helper to check if any item or entitlement is NOT included
  const hasUserAddedItems = section =>
    section?.some(
      item =>
        !item.included || item.selectedEntitlements?.some(ent => !ent.included),
    )

  // Cart is empty if NO user-added items exist
  return !(
    hasUserAddedItems(apps) ||
    hasUserAddedItems(centers) ||
    hasUserAddedItems(oneTimeFees)
  )
}

export const formatMeetingDate = scheduleDetail => {
  dayjs.extend(utc)
  const date = dayjs(scheduleDetail.thryvBookingDate)
    .tz(scheduleDetail.timeZone)
    .format('dddd, MMMM D [@] h:mm A z')
  return date

  // const dateStr = '2025-10-09T13:15:00-05:00'

  // const formatted = dayjs(dateStr)
  //   .tz('America/Chicago')
  //   .format('dddd, MMMM D [@] h:mm A z')
  // return formatted
  // const date = new Date(thryvBookingDate)
  // // Example: "Tuesday, October 9 @ 1:15PM CST"
  // const options = {
  //   weekday: 'long',
  //   month: 'long',
  //   day: 'numeric',
  //   hour: 'numeric',
  //   minute: '2-digit',
  //   hour12: true,
  //   timeZoneName: 'short', // gives CST/CDT, etc.
  // }
  // const formatted = new Intl.DateTimeFormat('en-US', options).format(date)
  // // Remove seconds and use your target sentence style
  // return `${formatted.replace(',', '').replace(' at', ' @')}`
}

// --- Email Validation Helpers ---
const emailRegex = /^(?!.*\+)[^\s@]+@[^\s@]+\.[^\s@]+$/
export const validateEmailLocally = email => {
  if (!emailRegex.test(email)) return 'Enter valid email address'
  if (email.includes('@thryv.com')) return 'Thryv addresses are not allowed'
  return ''
}

// --- URL / Domain Validation Helpers ---

const ERROR_MESSAGES = {
  required: 'This field is required',
  invalid: 'Please enter a valid domain',
}

const FORBIDDEN_DOMAINS = ['google', 'bing', 'twitter', 'facebook', 'amazon']
const ALLOWED_DOMAINS = ['yelp.com', 'yellowpages.com', 'yellowpages.com.au']

// common TLDs
const VALID_TLDS = [
  'com',
  'net',
  'org',
  'biz',
  'info',
  'io',
  'co',
  'us',
  'in',
  'uk',
  'ca',
  'au',
  'nz',
  'de',
  'fr',
  'jp',
  'cn',
  'es',
  'it',
]

export const validateUrlLocally = (domainString = '', validExtensionList) => {
  // nosemgrep: nodejs_scan.javascript-dos-rule-regex_dos
  const domain = domainString.trim().toLowerCase()
  let extentionList = validExtensionList || VALID_TLDS

  if (!domain) return ERROR_MESSAGES.required

  const hasPathOrQuery = /[/?#]/.test(domain)
  const isAllowedBusinessUrl = ALLOWED_DOMAINS.some(d => domain.includes(d))
  if (hasPathOrQuery && !isAllowedBusinessUrl) {
    return `${ERROR_MESSAGES.invalid}. No text is allowed after '.com' or similar.`
  }

  // nosemgrep: nodejs_scan.javascript-dos-rule-regex_dos
  const normalized = domain
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .split(/[/?#]/)[0]

  for (const d of FORBIDDEN_DOMAINS) {
    if (
      normalized === `${d}.com` ||
      normalized.includes(`.${d}.`) ||
      normalized.startsWith(`${d}.`) ||
      normalized.endsWith(`${d}.com`)
    ) {
      return `${ERROR_MESSAGES.invalid}. The domain "${d}" is not allowed.`
    }
  }

  for (const allowed of ALLOWED_DOMAINS) {
    if (domain.includes(allowed)) return ''
  }

  const labels = normalized.split('.')
  for (const label of labels) {
    if (!label) return `${ERROR_MESSAGES.invalid}. Domain has empty URL.`
    if (label.length > 63)
      return `${ERROR_MESSAGES.invalid}. URL "${label}" exceeds 63 characters.`
    if (label.startsWith('-') || label.endsWith('-'))
      return `${ERROR_MESSAGES.invalid}. URLs cannot start or end with '-'.`
  }

  const tld = labels[labels.length - 1]
  if (!extentionList.includes(tld)) {
    return `${ERROR_MESSAGES.invalid}. "${tld}" is not a recognized domain extension.`
  }

  const domainRegex = /^(?!-)[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,63}$/i
  if (!domainRegex.test(normalized)) {
    return `${ERROR_MESSAGES.invalid}. Domain format is not valid.`
  }

  // const doubleTldRegex = /\.(com|net|org|info|biz|co|us|in|uk|au|ca|io){2,}$/i
  const escapedExtensions = extentionList.map(ext =>
    ext.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
  )
  // nosemgrep: eslint.detect-non-literal-regexp
  const doubleTldRegex = new RegExp(
    `\\.(${escapedExtensions.join('|')}){2,}$`,
    'i',
  )
  if (doubleTldRegex.test(normalized)) {
    return `${ERROR_MESSAGES.invalid}. Domain cannot contain repeated TLDs like ".comcom" or similar.`
  }

  return ''
}

// const ERROR_MESSAGES = {
//   required: 'This field is required',
//   invalid: 'Please enter a valid domain',
// }

// const FORBIDDEN_DOMAINS = ['google', 'bing', 'twitter', 'facebook', 'amazon']

// export const validateUrlLocally = (domainString = '') => {
//   // 1. Empty check
//   if (!domainString.trim()) {
//     return ERROR_MESSAGES.required
//   }

//   // 2. Forbidden domains
//   for (const domain of FORBIDDEN_DOMAINS) {
//     if (
//       domainString.includes(`.${domain}.`) ||
//       domainString.startsWith(`${domain}.`) ||
//       domainString.includes(`${domain}.com`) ||
//       domainString.endsWith('yelp.com')
//     ) {
//       return `${ERROR_MESSAGES.invalid}. The domain ${domain} is not allowed.`
//     }
//   }

//   // 3. Extract plain URL without "www."
//   const splitUrl = domainString.split('www.')
//   const plainURL = splitUrl[splitUrl.length - 1]
//   const label = plainURL.slice(0, plainURL.indexOf('.'))

//   if (
//     label.length > 63 ||
//     label.startsWith('-') ||
//     domainString.startsWith('-')
//   ) {
//     return `${ERROR_MESSAGES.invalid}. Max domain length is 63. The domain cannot start or end with '-'.`
//   }

//   // 4. Regex for domain + allowed cases
//   const regex =
//     /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,6}|((yelp|yellowpages)\.com(\.au)?)\/[a-zA-Z0-9\-/?=.+]+)$/

//   // /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,6})(\/)?$/

//   if (!regex.test(domainString)) {
//     return `${ERROR_MESSAGES.invalid}. No text is allowed after '.com' or similar.`
//   }

//   // valid
//   return ''
// }

// src/utils/planEntitlementUtils.js

/**
 * Updates plan addons, one-time fees, and UI state (via setGroups)
 * when an entitlement (addon) is toggled.
 *
 * Specifically handles "Professionally Designed Website" entitlement
 * and its dependent "Website Design Fee".
 */
export const handleThryDesign2IfPlusSelected = ({
  item,
  filteredGroup,
  setCatalogGroups,
}) => {
  const selectedGroup = filteredGroup.find(g => g.key === item.groupKey)
  if (!selectedGroup) return

  const mcPlusPlan = selectedGroup.items.filter(
    i => i.code === 'mc_plus-5-thryv-m2m',
  )

  // === mc_plus logic ===
  if (mcPlusPlan.length > 0) {
    // Update catalog groups to remove 'thryvdesg2' entitlement from mc_pro
    setCatalogGroups(prevGroups =>
      prevGroups.map(group => {
        if (group.key === 'marketingCenter') {
          const updatedItems = group.items.map(i => {
            if (i.code === 'mc_pro-5-thryv-m2m' && i.entitlements) {
              return {
                ...i,
                entitlements: i.entitlements.filter(
                  a => a.code !== 'thryvdesg2',
                ),
              }
            }
            return i
          })

          return {...group, items: updatedItems}
        }
        return group
      }),
    )
  }
}

export const handleThryDesign2IfPlanRemoved = setCatalogGroups => {
  setCatalogGroups(prevGroups =>
    prevGroups.map(group => {
      if (group.key === 'marketingCenter') {
        const updatedItems = group.items.map(i => {
          if (i.code === 'mc_pro-5-thryv-m2m' && i.entitlements) {
            return {
              ...i,
              entitlements: i.entitlements.filter(a => a.code !== 'thryvdesg2'),
            }
          }
          return i
        })

        return {...group, items: updatedItems}
      } else {
        return group
      }
      // Find mc_pro plan in this group
    }),
  )
}

export const handleRemoveWebDesignFee = ({setGroups}) => {
  // Update UI (remove fee entitlement)
  setGroups(prev =>
    prev.map(g =>
      g.key !== 'marketingCenter'
        ? g
        : {
            ...g,
            items: g.items.map(i =>
              i.code === 'mc_pro-5-thryv-m2m'
                ? {
                    ...i,
                    entitlements: i.entitlements?.filter(
                      e => e.code !== WEBSITE_DESIGN_FEE_ENTITLEMENT.code,
                    ),
                  }
                : i,
            ),
          },
    ),
  )
}

export const checkNGetNewItemInCartGroups = cartGroups => {
  const addedItemsToCart = {}
  let hasMcPlanAlreadyAdded = false

  if (!cartGroups || cartGroups.length === 0) {
    return {addedItemsToCart: {}}
  }
  cartGroups.forEach(group => {
    let hasNewItem = false
    group?.items?.forEach(item => {
      const itemIsNew = !item.isAlreadyAdded
      const hasNewAddon = item.addons?.some(addon => !addon.isAlreadyAdded)

      //  Check if MC Pro is already added
      if (
        (item.code === 'mc_pro-5-thryv-m2m' ||
          item.code === 'mc_plus-5-thryv-m2m') &&
        item.isAlreadyAdded
      ) {
        hasMcPlanAlreadyAdded = true
      }

      if (itemIsNew || hasNewAddon) {
        hasNewItem = true
      }
    })

    // Include group if new item/addon OR MC Pro already added
    if (hasNewItem) {
      addedItemsToCart[group.key] = true
    }
  })

  return {addedItemsToCart, hasMcPlanAlreadyAdded}
}

export const isOneTimeItem = code => String(code || '').endsWith('-ot')

export const buildCartEntity = ({
  kind, // "item" | "addon"
  source, // item or entitlement
  selectedCountry,
  quantity = 1,
  customFields = {},
  groupKey,
  section,
  previousItem,
  keepAddons = false,
  autoAdd = false,
  selectCentersAndApps,
}) => {
  const currency =
    countryCurrencyEnum[selectedCountry] ||
    source?.currency ||
    countryCurrencyEnum.US
  const matched = source?.currencies?.find(c => c.currency === currency)
  const unitAmount = matched?.unitAmount ?? source?.unitAmount ?? 0
  // ---------- ADDON ----------
  if (kind === 'addon') {
    return {
      code: source?.code ?? '',
      name: source?.name ?? '',
      currency,
      quantity,
      unitAmount,
      amount: unitAmount * quantity,
      isAlreadyAdded: false,
    }
  }

  // ---------- ITEM ----------
  const sameItem = previousItem?.code === source?.code
  let addons = keepAddons && sameItem ? previousItem?.addons || [] : []
  let group = {
    groupKey: groupKey ?? source?.groupKey ?? '',
    section: section ?? source?.section ?? '',
    code: source?.code ?? '',
    name: source?.name ?? '',
    currency,
    quantity,
    unitAmount,
    amount: unitAmount * quantity,
    addons,
    customFields,
    autoAddCenters: source?.autoAddCenters,
  }

  // Auto add for mc pro ent-5gmb entitlement
  if (autoAdd === 'GMB' && source?.code === 'mc_pro-5-thryv-m2m') {
    const gbpo = source?.entitlements?.find(e => e.code === 'ent-5-gmb')
    if (gbpo) {
      const gmbAddon = buildCartEntity({
        kind: 'addon',
        source: gbpo,
        selectedCountry,
      })
      const exists = group.addons.some(a => a.code === gmbAddon.code)
      if (!exists) group.addons = [...group.addons, gmbAddon]
    }
  }

  // auto add for keap_max entitlements
  if (autoAdd === 'keap-required-entitlements') {
    const autoAddRequiredEntitlements = source.entitlements.filter(
      entitlement => entitlement.isRequired,
    )

    autoAddRequiredEntitlements.forEach(entitlement => {
      const requiredAddon = buildCartEntity({
        kind: 'addon',
        source: entitlement,
        selectedCountry,
        quantity: entitlement?.quantity || 1,
      })
      const exists = group.addons.some(a => a.code === requiredAddon.code)
      if (!exists) group.addons = [...group.addons, requiredAddon]
    })
  }

  return group
}
